import type {XYPosition} from 'reactflow';

import {pluralize} from 'BenchlingUtil';
import notifications from 'notifications';
import {deleteStepsWithInferredEdges} from 'pipelines/network/deleteStepWithInferredEdges';
import * as PipelineDAO from 'pipelines/network/PipelineDAO';
import {PipelineGraphQL} from 'pipelines/PipelineTypes';

import {AnalysisFlowchartAction} from '../AnalysisFlowchartActionTypes';
import {copyStepsToClipboard} from '../copy-paste/copyStepsToClipboard';
import {pasteStepsFromClipboard} from '../copy-paste/pasteStepsFromClipboard';
import {connectSteps} from './connectSteps';
import {rerunSteps} from './rerunSteps';

type Pipeline = Pick<PipelineGraphQL, 'steps' | 'id'>;

/**
 * Pass through the return value to the parent of <AnalysisFlowchart>. When `createBranch` is set (drag from
 * an output handle onto empty canvas), the new step branches off `stepData` and pins at `position`.
 */
const openAddStep = (
  pipeline: Pipeline,
  stepId: string,
  stepDataName: string,
  createBranch?: boolean,
  position?: XYPosition
): Promise<{stepId: string; stepDataName: string; createBranch?: boolean; position?: XYPosition}> =>
  Promise.resolve({stepId, stepDataName, createBranch, position});

/** Pass through to the parent of <AnalysisFlowchart> to open add step sidebar for source steps. */
const openAddSourceStep = (pipeline: Pipeline): Promise<void> => Promise.resolve();

/**
 * Call shared `add-and-update-steps` endpoint to delete steps and re-connect the remaining steps.
 */
async function deleteSteps(pipeline: Pipeline, ...stepIds: Array<string>): Promise<void> {
  await deleteStepsWithInferredEdges(pipeline, stepIds);
  notifications.success(`Deleted ${stepIds.length} ${pluralize(stepIds.length, 'step')}.`);
}

/**
 * Call shared `pipelines:` endpoint to duplicate steps.
 */
async function duplicateSteps(
  pipeline: Pipeline,
  ...stepIds: Array<string>
): Promise<{stepIds: Array<string>}> {
  return await PipelineDAO.copyPipelineSteps(pipeline.id, {stepIds});
}

/** Pass through the return value to the parent of <AnalysisFlowchart>. */
const openStepConfiguration = (pipeline: Pipeline, stepId: string): Promise<{stepId: string}> =>
  Promise.resolve({stepId});

/**
 * Logic to execute each `AnalysisFlowchartAction`.
 *
 * The `onAction` callback (provided by the parent of <AnalysisFlowchart> in `AnalysisFlowchartActionInfo`)
 * is called after these internal functions complete.
 */
export const INTERNAL_FLOWCHART_ACTION_FUNCTIONS = {
  [AnalysisFlowchartAction.CONNECT_STEPS]: connectSteps,
  [AnalysisFlowchartAction.COPY_TO_CLIPBOARD]: copyStepsToClipboard,
  [AnalysisFlowchartAction.PASTE_FROM_CLIPBOARD]: pasteStepsFromClipboard,
  [AnalysisFlowchartAction.DELETE_STEPS]: deleteSteps,
  [AnalysisFlowchartAction.DUPLICATE_STEPS]: duplicateSteps,
  [AnalysisFlowchartAction.OPEN_ADD_STEP]: openAddStep,
  [AnalysisFlowchartAction.OPEN_ADD_SOURCE_STEP]: openAddSourceStep,
  [AnalysisFlowchartAction.OPEN_STEP_CONFIGURATION]: openStepConfiguration,
  [AnalysisFlowchartAction.RERUN_STEPS]: rerunSteps,
} satisfies Record<
  AnalysisFlowchartAction,
  (pipeline: Pipeline, ...args: Array<unknown>) => Promise<unknown>
>;

/**
 * True if the action function doesn't require a GraphQL refresh or loading screen.
 */
export const INTERNAL_FLOWCHART_ACTION_SKIP_REFRESH: Partial<Record<AnalysisFlowchartAction, boolean>> = {
  [AnalysisFlowchartAction.COPY_TO_CLIPBOARD]: true,
  [AnalysisFlowchartAction.OPEN_ADD_STEP]: true,
  [AnalysisFlowchartAction.OPEN_ADD_SOURCE_STEP]: true,
  [AnalysisFlowchartAction.OPEN_STEP_CONFIGURATION]: true,
};

/**
 * True if the action function does not modify any steps and can be performed on a read-only analysis.
 */
export const INTERNAL_FLOWCHART_ACTION_IS_READ_ONLY: Partial<Record<AnalysisFlowchartAction, boolean>> = {
  [AnalysisFlowchartAction.COPY_TO_CLIPBOARD]: true,
  [AnalysisFlowchartAction.OPEN_STEP_CONFIGURATION]: true,
};
